import { useCallback } from "react";
import {
  type EventTransformerHooks,
  logNotFoundTrackingProperties,
  useEventTracking,
  type UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import {
  FlightsPriceFreezeExerciseTrackingEvents,
  type FlightsPriceFreezeHTSEvent,
  FLIGHTS_PRICE_FREEZE_PRODUCT_TYPE,
} from "../events";
import { getTransformedEventProperties } from "../transformedEventProperties";
import {
  FlightsPriceFreezeExerciseRawEventName,
  type FlightsPriceFreezeRawEvent,
} from "../types";

const useFlightsPriceFreezeExerciseEntryClickedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(() => {
    const eventProperties = getTransformedEventProperties({
      trackingAttributes: getAllAttributes(),
    });

    return {
      name: FlightsPriceFreezeExerciseTrackingEvents.PRICE_FREEZE_SELECTED_EXERCISE_ENTRY_POINT,
      properties: eventProperties.flightPriceFreezeProperties,
    };
  }, [getAllAttributes]);
};

const useFlightsPriceFreezeExerciseMissionControlLoadedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(() => {
    const eventProperties = getTransformedEventProperties({
      trackingAttributes: getAllAttributes(),
    });

    return {
      name: FlightsPriceFreezeExerciseTrackingEvents.PRICE_FREEZE_VIEWED_EXERCISE_MISSION_CONTROL,
      properties: eventProperties.flightPriceFreezeProperties,
    };
  }, [getAllAttributes]);
};

const useFlightsPriceFreezeExerciseContinueClickedTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(() => {
    const eventProperties = getTransformedEventProperties({
      trackingAttributes: getAllAttributes(),
    });

    return {
      name: FlightsPriceFreezeExerciseTrackingEvents.PRICE_FREEZE_SELECTED_EXERCISE_CONTINUE,
      properties: eventProperties.flightPriceFreezeProperties,
    };
  }, [getAllAttributes]);
};

const useFlightsPriceFreezeExerciseNoAvailabilityTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(() => {
    const eventProperties = getTransformedEventProperties({
      trackingAttributes: getAllAttributes(),
    });

    return {
      name: FlightsPriceFreezeExerciseTrackingEvents.PRICE_FREEZE_NO_AVAILABILITY,
      properties: eventProperties.flightPriceFreezeProperties,
    };
  }, [getAllAttributes]);
};

const useFlightsPriceFreezeExerciseRequestRefundTransformer: UseInstantTransformEvent<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = () => {
  return useCallback((event) => {
    if (event.properties?.price_freeze_refund == null) {
      logNotFoundTrackingProperties(
        "transformersByEventNameExercise",
        "flights price freeze"
      );
    }

    return {
      name: FlightsPriceFreezeExerciseTrackingEvents.PRICE_FREEZE_REQUEST_REFUND,
      properties: {
        product_type: FLIGHTS_PRICE_FREEZE_PRODUCT_TYPE,
        price_freeze_refund: Boolean(event.properties?.price_freeze_refund),
      },
    };
  }, []);
};

export const FLIGHTS_PRICE_FREEZE_EXERCISE_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  FlightsPriceFreezeRawEvent,
  FlightsPriceFreezeHTSEvent
> = {
  [FlightsPriceFreezeExerciseRawEventName.FlightsPriceFreezeExerciseEntryClicked]:
    [useFlightsPriceFreezeExerciseEntryClickedTransformer],
  [FlightsPriceFreezeExerciseRawEventName.FlightsPriceFreezeExerciseMissionControlLoaded]:
    [useFlightsPriceFreezeExerciseMissionControlLoadedTransformer],
  [FlightsPriceFreezeExerciseRawEventName.FlightsPriceFreezeExerciseContinueClicked]:
    [useFlightsPriceFreezeExerciseContinueClickedTransformer],
  [FlightsPriceFreezeExerciseRawEventName.FlightsPriceFreezeExerciseNoAvailability]:
    [useFlightsPriceFreezeExerciseNoAvailabilityTransformer],
  [FlightsPriceFreezeExerciseRawEventName.FlightsPriceFreezeExerciseRequestRefund]:
    [useFlightsPriceFreezeExerciseRequestRefundTransformer],
};
